import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { finalize, map, shareReplay, tap } from "rxjs/operators";
import { APIService } from "../api/api.service";

/**
 * Synchronous + reactive access to the caller's effective RBAC permission
 * ids for the active tenant. Populated once via load() (called after
 * login/tenant is known); has()/has$() answer UI-enforcement checks
 * (show/hide a button, enable/disable a field, gate a route later).
 *
 * This is UX only, never an authorization boundary (dashboard-security
 * skill, rule #1): a missing id here just hides a control. The API is the
 * real check and re-validates every write regardless of what this set says.
 *
 * No PII: the set holds permission ids only (e.g. "clientrole:write"), never
 * user data.
 *
 * Tenant switch: AuthService.changeTenant() does a hard `location.replace()`
 * (see auth.service.ts), which reloads the whole SPA and re-creates every
 * root-provided service, including this one, with an empty set. That already
 * satisfies "clear on tenant switch" for the current switcher. clear() is
 * still exposed (and used on logout / auth failure) so a future in-place
 * tenant switch — one that stops doing a full reload — can call clear()
 * then load() without any change to this service. See task-2.1-report.md.
 *
 * Readiness: permissions load async after login, so a consumer that decides
 * access (PermissionGuard) cannot trust has()/has$() until the effective set
 * has been fetched at least once — otherwise a fresh navigation/refresh reads
 * an empty set as "denied". ready$ exists for that: it starts false and
 * flips to true (and stays true) the first time load() succeeds. See
 * task-2.3-report.md / permission.guard.ts for the consumer.
 *
 * load() is shared and deduped: concurrent callers (e.g. PermissionGuard and
 * the sidenav both triggering it on the same navigation) get the same
 * in-flight request instead of firing one GET each, and once the set has
 * loaded successfully a later load() resolves immediately from the cached
 * set with no further HTTP call. A failed load() is NOT cached — the
 * in-flight observable is dropped so the very next load() call retries
 * against the API; nothing retries on its own.
 */
@Injectable({ providedIn: "root" })
export class PermissionService {
  private readonly permissions$: BehaviorSubject<Set<string>> = new BehaviorSubject<Set<string>>(new Set<string>());
  private readonly loaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private inFlight$: Observable<string[]> | undefined;

  /**
   * Emits true once the effective permission set has been loaded at least
   * once (i.e. after the first successful load()); false before that, and
   * reset to false by clear(). A failed load() does NOT flip this to true —
   * the set genuinely isn't loaded yet. Note this does not retry by itself:
   * a consumer that only watches ready$ will sit at false forever after a
   * failure unless something calls load() again (PermissionGuard does, by
   * driving off load() directly instead of ready$ — see permission.guard.ts).
   */
  public readonly ready$: Observable<boolean> = this.loaded$.asObservable();

  public constructor(private readonly api: APIService) {}

  /**
   * Synchronous counterpart to ready$, for consumers that cannot subscribe (e.g. a
   * template method re-evaluated on every change-detection cycle, like
   * DatatableComponent.shouldShowAction). Same semantics as ready$: false until the first
   * successful load(), true afterward, reset to false by clear().
   */
  public get isReady(): boolean {
    return this.loaded$.value;
  }

  /**
   * Fetches the caller's effective permission ids for the active tenant
   * (GET v1/clientrole/effective, via ClientRolesService) and populates the
   * set. Call after login/tenant is known; safe to call repeatedly/from
   * multiple call sites (see class doc: shared + deduped + cached-after-
   * success). On failure the set is left as-is (empty, if this is the first
   * call) and the error is propagated on the returned observable so the
   * caller decides how to react (PermissionGuard redirects; it never hangs).
   */
  public load(): Observable<string[]> {
    if (this.loaded$.value) {
      return of(Array.from(this.permissions$.value));
    }
    if (!this.inFlight$) {
      this.inFlight$ = this.api.clientRoles.getEffectivePermissions().pipe(
        tap((ids: string[]) => {
          this.permissions$.next(new Set<string>(ids || []));
          this.loaded$.next(true);
        }),
        shareReplay(1),
        finalize((): void => {
          this.inFlight$ = undefined;
        })
      );
    }
    return this.inFlight$;
  }

  /** Synchronous check against the currently loaded set. */
  public has(id: string): boolean {
    return this.permissions$.value.has(id);
  }

  /** Reactive check against the loaded set; emits again on every load()/clear(). */
  public has$(id: string): Observable<boolean> {
    return this.permissions$.pipe(map((ids: Set<string>) => ids.has(id)));
  }

  /** Resets the set to empty and readiness to false. Call on tenant switch / logout (see class doc). */
  public clear(): void {
    this.permissions$.next(new Set<string>());
    this.loaded$.next(false);
  }
}
